import { useImperativeHandle } from 'react';
import * as v from 'valibot';
import { type AnyFieldApi, useForm } from '@tanstack/react-form';

import { Button, InputField } from '@nvision/ui';

import { type ProductSearchFormValues, productSearchFormSchema } from '../../../models';

import styles from './product-search-form.module.css';

export type SearchFormRef = {
  submit: () => void;
  canSubmit: () => void;
  reset: () => void;
};

interface ProductSearchFormProps {
  formRef: React.RefObject<SearchFormRef>;
  handleSubmit: (values: ProductSearchFormValues) => void;
  handleReset: () => void;
}

const getDefaultSearchFormValues = (): ProductSearchFormValues =>
  v.getDefaults(productSearchFormSchema) as unknown as ProductSearchFormValues;

export const ProductSearchForm: React.FC<ProductSearchFormProps> = ({ formRef, handleSubmit, handleReset }) => {
  const form = useForm({
    defaultValues: getDefaultSearchFormValues(),
    onSubmit: async (props) => {
      handleSubmit(props.value);
    },
    validators: {
      onChange: productSearchFormSchema,
    },
  });

  useImperativeHandle(formRef, () => ({
    canSubmit: () => form.state.canSubmit,
    reset: () => {
      form.reset(getDefaultSearchFormValues());
      handleReset();
    },
    submit: () => {
      form.handleSubmit();
    },
  }));

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit();
  };

  const getError = (field: AnyFieldApi) =>
    field.state.meta.isTouched
      ? field.state.meta.errors
          .map((e: { message?: string }) => e?.message)
          .filter(Boolean)
          .join('. ')
      : undefined;

  return (
    <form className={styles.form} onSubmit={onSubmit}>
      <div className={styles.fieldsContainer}>
        <form.Field name="productCode">
          {(field) => (
            <InputField
              className={styles.field}
              value={field.state.value ?? ''}
              clearable
              onChange={(e) => field.handleChange(e.target.value)}
              label="Product Code"
              placeholder="Enter"
              size="sm"
              errorMessage={getError(field)}
            />
          )}
        </form.Field>
        <form.Field name="productDesc">
          {(field) => (
            <InputField
              className={styles.field}
              value={field.state.value ?? ''}
              clearable
              onChange={(e) => field.handleChange(e.target.value)}
              label="Product Description"
              placeholder="Enter"
              size="sm"
              errorMessage={getError(field)}
            />
          )}
        </form.Field>
      </div>
      <Button type="submit">Search</Button>
      <Button type="button" outline secondary onClick={() => formRef.current?.reset()}>
        Reset
      </Button>
    </form>
  );
};
